import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type {
  LetterCta,
  LetterKind,
  LetterState,
  LetterTaster,
  OvertureDecision,
  PostageClass,
  PostageSource,
  RedactionKind,
} from '@toiyp/shared';
import { API_ORIGIN } from '../../config';
import { withUnlockHeader } from '../hidden/hiddenSlice';

/** The letter as the API returns it. Mirrors the `letters` table (§4.3). */
export interface ApiLetter {
  id: string;
  relationshipId: string;
  authorId: string;
  state: LetterState;
  body: string;
  sealedAt: string | null;
  /** When the post van picks up the letter (next business-hours collection slot after sealing). */
  collectedAt: string | null;
  deliverAt: string | null;
  deliveredAt: string | null;
  openedAt: string | null;
  answeredAt: string | null;
  readReceipt: { letterId: string; openedAt: string; readCount: number; reads: string[] } | null;
  /** Every time the recipient opened it (§7.x). */
  reads: string[];
  /**
   * `letter` is correspondence between two members. `overture` is first contact
   * from an address with no account here: it cannot be answered, only accepted
   * or declined, and the UI must not offer Reply for it.
   */
  kind: LetterKind;
  /** The subject line the sender used, when it arrived by mail. */
  subject: string | null;
  /** The one button a Post Office introduction carries; null everywhere else. */
  cta: LetterCta | null;
  /** Set once the recipient has decided an overture; null while it is pending. */
  overtureDecision: OvertureDecision | null;
  /** When that decision was recorded. */
  decidedAt: string | null;
  /**
   * The external address an overture arrived from, and which member it was
   * aimed at — present only on the recipient's side. Null on every letter, and
   * on the sender's own view of their overture.
   */
  overture: { fromEmail: string; toUserId: string } | null;
  /** The other person in the relationship (the author of an inbox letter). */
  peer: {
    userId: string;
    handle: string | null;
    displayName: string | null;
    bio: string | null;
    /** Their birthday as `MM-DD` — never the year. */
    birthday?: string | null;
  } | null;
  /** Deterministic score + fixed product copy (§8.7), or null when not analysed. */
  quality: {
    composite: number | null;
    degraded: boolean;
    algorithmVersion: string | null;
    why: string[];
    /** tone label for the peek (warm / neutral / tense). */
    sentiment: string | null;
  } | null;
  /** True when the recipient sees a snippet, not the full body (§7.x). */
  peek: boolean;
  /**
   * How often the recipient came back to this letter before it arrived (§7.x)
   * — the writer's to read, as a number. Null on every letter in the
   * recipient's own view: your visits are not your correspondent's business
   * to see reflected back at you.
   */
  peekCount: number | null;
  /**
   * What a peek may say about a letter that has not arrived (F5): size and
   * time-to-write as fixed phrases — never the numbers behind them. Null on
   * every letter the reader is allowed to read whole.
   */
  taster: LetterTaster | null;
  /**
   * Contact details withheld from `body` because this pair had not reached
   * `trusted`. Null when nothing was held back — details open up on promotion,
   * so no letter is ever permanently damaged by it.
   */
  redactedKinds: RedactionKind[] | null;
  /**
   * Short phrases appended to an arrived-but-unanswered letter as nudges.
   * A nudge is never a new letter; it lives as a postscript on the last sent one.
   */
  addendums: { id: string; text: string; createdAt: string }[];
  /** The author's drawn sign-off as it was when sealed (an SVG path), or null. */
  signature: string | null;
  /**
   * When the recipient chose to say "got your letter, replying properly
   * soon". Never set by opening — null says nothing at all.
   */
  acknowledgedAt: string | null;
  /** The stamp the letter travelled on (docs/POSTAGE_PLAN.md). 2nd class is the free standard. */
  postageClass: PostageClass;
  postageSource: PostageSource;
  /**
   * On a seal response only: the class asked for was not available (lost since,
   * or the last stamp went elsewhere), so the letter went on the member's
   * default class instead — `postageClass` says which.
   */
  postageDowngraded?: boolean;
  /**
   * The artwork the stamp was printed with (custom stamps), or null for the
   * plain class stamp. Cosmetic: `postageClass` still says how it travelled.
   */
  stampDesign?: StampDesign | null;
  /** The paper it was written on (stationery), or null for plain paper. */
  stationery?: Stationery | null;
  /** The envelope it travelled in, or null for a plain one. */
  envelope?: Stationery | null;
  /**
   * Photo attachments (RT-4). `url` is a same-origin, gated endpoint
   * (/api/media/:id). Sending one needs trust; once it has arrived it is the
   * reader's to keep, unless the sender takes it back.
   */
  media: LetterMedia[];
}

export interface LetterMedia {
  id: string;
  kind: string;
  mime: string;
  width: number | null;
  height: number | null;
  url: string;
  /** The sender took it back: the picture is gone for both of them. */
  takenBack: boolean;
  /** The sender's line under the print, or null. */
  caption: string | null;
}

/** Longest caption the server takes, counted after whitespace is folded. */
export const MAX_CAPTION_CHARS = 120;

/** One stamp's artwork (custom stamps). `imageUrl` is same-origin under /api. */
export interface StampDesign {
  id: string;
  name: string;
  imageUrl: string;
  width: number | null;
  height: number | null;
  /** The book it comes from — set on a letter's stamp. */
  collectionName?: string;
}

/** The clear part of a sheet the words go in, as fractions of the image from each edge. */
export interface WritingArea {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

/** One letter paper (stationery). Cosmetic only, like a stamp design. */
export interface Stationery {
  id: string;
  /** `paper` is written on; `envelope` is the pattern a letter travels in. */
  kind: 'paper' | 'envelope';
  name: string;
  imageUrl: string;
  width: number;
  height: number;
  area: WritingArea;
  ink: string;
  /** Envelopes: pattern size, 1 = the image's width across the envelope. */
  scale: number;
  /** The book it comes from — set on a letter's paper. */
  collectionName?: string;
}

/** free | plus | gift | admin open a collection; locked is a preview. */
export type StampAccess = 'free' | 'plus' | 'gift' | 'admin' | 'locked';

export interface StampCollection {
  id: string;
  name: string;
  description: string;
  plusOnly: boolean;
  access: StampAccess;
  giftedBy: { handle: string | null; displayName: string | null } | null;
  designs: StampDesign[];
  /** Letter paper in the same book, with the same access as its stamps. */
  papers: Stationery[];
  /** Envelopes in the same book. */
  envelopes: Stationery[];
}

export interface StampGift {
  id: string;
  /** Null on a gift held behind a link nobody has opened yet. */
  relationshipId: string | null;
  collectionId: string;
  collectionName: string;
  state: 'waiting' | 'travelling' | 'held' | 'delivered' | 'withdrawn' | 'expired';
  createdAt: string;
  /**
   * `card`: posted on its own with a note; `letter`: rides my next letter;
   * `newcomer`: held for someone not on toiyp yet, until they join.
   */
  kind: 'card' | 'letter' | 'newcomer';
  note: string | null;
  collectedAt: string | null;
  deliverAt: string | null;
  /** A gift for someone new: the address it went to, or its link to share again; when it comes back. */
  invite: { email: string | null; url: string | null; expiresAt: string | null } | null;
}

/** A stamp book given to someone new: `member` means the address was already on toiyp. */
export interface NewcomerGift {
  gift: StampGift;
  via: 'email' | 'link' | 'member';
}

/**
 * A gift card in one pair's postal record. The recipient sees only that
 * something is on its way until it arrives (`collectionName`/`note` null).
 */
export interface StampParcel {
  id: string;
  mine: boolean;
  state: 'travelling' | 'delivered';
  sentAt: string;
  collectedAt: string | null;
  deliverAt: string | null;
  deliveredAt: string | null;
  collectionName: string | null;
  note: string | null;
  designs: StampDesign[];
}

/** GET /me/stamps — what this member can stick on a letter, and give. */
export interface MyStamps {
  plus: boolean;
  collections: StampCollection[];
  gifting: { left: number; perMonth: number; nextOn: string };
  outgoing: StampGift[];
}

export type PostageClassState = 'available' | 'locked' | 'paused' | 'empty' | 'hidden';

/** One pair's postage, as this member sees it. ETAs come from the server's own window function. */
export interface PostageView {
  enabled: boolean;
  rulesVersion: string;
  classes: {
    class: PostageClass;
    label: string;
    state: PostageClassState;
    hint: string | null;
    balance: number | null;
    collectedAt: string;
    deliverAt: string;
  }[];
  preferred: PostageClass;
  expressBalance: number;
  offer: {
    class: PostageClass;
    reasons: string[];
    includesFirst: boolean;
    expressBalance: number;
  } | null;
}

/** The member's stamp book — Express stamps per correspondent, passes, history. */
export interface StampBookView {
  enabled: boolean;
  cap: number;
  monthlyAllowance: number;
  nextAllowanceOn: string;
  pages: {
    relationshipId: string;
    express: number;
    expressEarned: boolean;
    firstClass: boolean;
    lastEarnedAt: string | null;
  }[];
  anyLetter: { express: number; first: number };
  /** Special editions (slice 4): welcome stamps and milestone designs — never postage. */
  special: {
    welcome: {
      kind: 'welcomed_by' | 'brought';
      counterpart: { handle: string | null; displayName: string | null } | null;
      at: string;
    }[];
    milestones: { milestone: number; name: string; at: string }[];
  };
  history: {
    at: string;
    class: PostageClass;
    delta: number;
    kind: 'allowance' | 'spent' | 'refund' | 'other';
    relationshipId: string | null;
  }[];
}

export const lettersApi = createApi({
  reducerPath: 'lettersApi',
  // Proxied to the API in dev (vite.config.ts), so the browser stays same-origin.
  baseQuery: fetchBaseQuery({
    baseUrl: `${API_ORIGIN}/api`,
    credentials: 'include',
    // Hidden pairs' letters come back only while the vault is open.
    prepareHeaders: (headers, { getState }) => withUnlockHeader(headers, getState),
  }),
  tagTypes: ['Letter', 'Postage', 'Stamps'],
  // Coming back to the app (tab focus, reopened PWA, network back) shows fresh mail.
  refetchOnFocus: true,
  refetchOnReconnect: true,
  endpoints: (b) => ({
    /** Inbox: peeks for sealed letters, then what the server is willing to show. */
    inbox: b.query<ApiLetter[], void>({
      query: () => '/letters?box=inbox',
      providesTags: ['Letter'],
    }),
    outbox: b.query<ApiLetter[], void>({
      query: () => '/letters?box=outbox',
      providesTags: ['Letter'],
    }),
    /**
     * One conversation, both directions, chronological — the messaging view's
     * thread. Fetched as inbox+outbox scoped to the relationship and merged.
     */
    thread: b.query<ApiLetter[], { relationshipId: string }>({
      async queryFn({ relationshipId }, _api, _opts, baseQuery) {
        const [inbox, outbox] = await Promise.all([
          baseQuery({ url: `/letters?box=inbox&relationshipId=${relationshipId}` }),
          baseQuery({ url: `/letters?box=outbox&relationshipId=${relationshipId}` }),
        ]);
        if (inbox.error) return { error: inbox.error };
        if (outbox.error) return { error: outbox.error };
        const merged = [...(inbox.data as ApiLetter[]), ...(outbox.data as ApiLetter[])].sort(
          (a, b) =>
            (a.sealedAt ? new Date(a.sealedAt).getTime() : 0) -
            (b.sealedAt ? new Date(b.sealedAt).getTime() : 0),
        );
        return { data: merged };
      },
      providesTags: ['Letter'],
    }),
    seal: b.mutation<
      ApiLetter,
      {
        relationshipId: string;
        body: string;
        postage?: PostageClass;
        composeMs?: number;
        stampDesignId?: string;
        stationeryId?: string;
        envelopeId?: string;
      }
    >({
      query: (body) => ({ url: '/letters', method: 'POST', body }),
      // A stamp book waiting for this pair leaves with the letter.
      invalidatesTags: ['Letter', 'Postage', 'Stamps'],
    }),
    open: b.mutation<ApiLetter, { id: string }>({
      query: ({ id }) => ({ url: `/letters/${id}/open`, method: 'POST' }),
      invalidatesTags: ['Letter'],
    }),
    /**
     * "I came back to this sealed letter" (§7.x). Deliberately does not
     * invalidate `Letter`: the count it returns is the writer's number, and
     * this is the reader's device — refetching their thread would buy nothing.
     */
    touch: b.mutation<{ peekCount: number }, { id: string }>({
      query: ({ id }) => ({ url: `/letters/${id}/touch`, method: 'POST' }),
    }),
    answer: b.mutation<
      ApiLetter,
      {
        id: string;
        body: string;
        postage?: PostageClass;
        composeMs?: number;
        stampDesignId?: string;
        stationeryId?: string;
        envelopeId?: string;
      }
    >({
      query: ({ id, body, postage, composeMs, stampDesignId, stationeryId, envelopeId }) => ({
        url: `/letters/${id}/answer`,
        method: 'POST',
        body: { body, postage, composeMs, stampDesignId, stationeryId, envelopeId },
      }),
      invalidatesTags: ['Letter', 'Postage', 'Stamps'],
    }),
    revise: b.mutation<ApiLetter, { id: string; body: string }>({
      query: ({ id, body }) => ({ url: `/letters/${id}`, method: 'PATCH', body: { body } }),
      invalidatesTags: ['Letter'],
    }),
    cancel: b.mutation<ApiLetter, { id: string }>({
      query: ({ id }) => ({ url: `/letters/${id}/cancel`, method: 'POST' }),
      // A cancelled Express letter puts its stamp back in the book (and a gift waits again).
      invalidatesTags: ['Letter', 'Postage', 'Stamps'],
    }),
    /**
     * Postage for one pair (docs/POSTAGE_PLAN.md): each class with a real ETA if
     * sealed now, the stamps in hand, the sticky default and any pending offer.
     */
    postage: b.query<PostageView, { relationshipId: string }>({
      query: ({ relationshipId }) => `/relationships/${relationshipId}/postage`,
      providesTags: ['Postage'],
      // ETAs are "if sealed now" — a composer left open should not quote a stale round.
      keepUnusedDataFor: 30,
    }),
    /** The stamp book. Hidden pairs come back only while the vault is open. */
    stampBook: b.query<StampBookView, void>({
      query: () => '/me/stamp-book',
      providesTags: ['Postage'],
    }),
    /** Custom stamps: collections I can use (and locked previews), gifts left, gifts on their way. */
    myStamps: b.query<MyStamps, void>({
      query: () => '/me/stamps',
      providesTags: ['Stamps'],
    }),
    /** Give a correspondent a stamp book; it rides my next letter to them. */
    /** Gift cards between me and one correspondent, for the thread. */
    stampParcels: b.query<StampParcel[], { relationshipId: string }>({
      query: ({ relationshipId }) => `/relationships/${relationshipId}/stamp-parcels`,
      providesTags: ['Stamps'],
    }),
    giftStampBook: b.mutation<
      StampGift,
      { relationshipId: string; collectionId: string; send?: 'letter' | 'card'; note?: string }
    >({
      query: (body) => ({ url: '/me/stamps/gifts', method: 'POST', body }),
      invalidatesTags: ['Stamps'],
    }),
    /** A stamp book for someone not on toiyp yet: an invite email, or (no address) a link to share. */
    giftStampBookToNewcomer: b.mutation<NewcomerGift, { collectionId: string; note: string; email?: string }>({
      query: (body) => ({ url: '/me/stamps/gifts/newcomer', method: 'POST', body }),
      invalidatesTags: ['Stamps'],
    }),
    withdrawStampGift: b.mutation<void, { id: string }>({
      query: ({ id }) => ({ url: `/me/stamps/gifts/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Stamps'],
    }),
    setPostagePreference: b.mutation<
      PostageView,
      { relationshipId: string; preferred?: 'second' | 'first'; seen?: boolean }
    >({
      query: ({ relationshipId, ...body }) => ({
        url: `/relationships/${relationshipId}/postage/preference`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Postage'],
    }),
    /**
     * Accept an overture: the stranger gets an account link by email. `note` is
     * optional — when given it is sealed and delivered immediately, and its
     * text becomes the teaser's first line in that email.
     */
    acceptOverture: b.mutation<
      { decision: OvertureDecision; to: string; noteLetterId: string | null },
      { id: string; note?: string | null }
    >({
      query: ({ id, note }) => ({
        url: `/mail/overtures/${id}/accept`,
        method: 'POST',
        body: { note: note ?? null },
      }),
      invalidatesTags: ['Letter'],
    }),
    /**
     * Decline an overture: no link, and nothing they send reaches the member
     * again. Reversible — see `undoDeclineOverture`.
     */
    declineOverture: b.mutation<
      { decision: OvertureDecision; to: string; noteLetterId: null },
      { id: string }
    >({
      query: ({ id }) => ({ url: `/mail/overtures/${id}/decline`, method: 'POST' }),
      invalidatesTags: ['Letter'],
    }),
    /** Undo a decline: reopen the decision and let their mail through again. */
    undoDeclineOverture: b.mutation<
      { decision: null; to: string; noteLetterId: null },
      { id: string }
    >({
      query: ({ id }) => ({ url: `/mail/overtures/${id}/undo-decline`, method: 'POST' }),
      invalidatesTags: ['Letter'],
    }),
    /** Append a short nudge phrase to an arrived-but-unanswered sent letter. */
    nudge: b.mutation<ApiLetter, { id: string; text: string }>({
      query: ({ id, text }) => ({ url: `/letters/${id}/nudge`, method: 'POST', body: { text } }),
      invalidatesTags: ['Letter'],
    }),
    /** "Got your letter, replying properly soon" — the recipient's choice, once. */
    acknowledge: b.mutation<ApiLetter, { id: string }>({
      query: ({ id }) => ({ url: `/letters/${id}/acknowledge`, method: 'POST' }),
      invalidatesTags: ['Letter'],
    }),
    /** Attach a photo to a still-sealed letter I wrote — `trusted` pairs only. */
    attachPhoto: b.mutation<LetterMedia, { id: string; data: string; mime: string; width?: number; height?: number; caption?: string }>({
      query: ({ id, ...body }) => ({ url: `/letters/${id}/media`, method: 'POST', body }),
      invalidatesTags: ['Letter'],
    }),
    /** Write, change or clear (null) the caption on a photo I sent, while its letter is sealed. */
    captionPhoto: b.mutation<LetterMedia, { id: string; caption: string | null }>({
      query: ({ id, caption }) => ({ url: `/media/${id}`, method: 'PATCH', body: { caption } }),
      invalidatesTags: ['Letter'],
    }),
    /** Take back a photo I sent — deleted for both of us; once arrived, the letter says one was there. */
    takeBackPhoto: b.mutation<LetterMedia | null, { id: string }>({
      query: ({ id }) => ({ url: `/media/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Letter'],
    }),
  }),
});

export const {
  useInboxQuery,
  useOutboxQuery,
  useThreadQuery,
  useSealMutation,
  useAcceptOvertureMutation,
  useDeclineOvertureMutation,
  useUndoDeclineOvertureMutation,
  useOpenMutation,
  useTouchMutation,
  useAnswerMutation,
  useReviseMutation,
  useCancelMutation,
  usePostageQuery,
  useStampBookQuery,
  useMyStampsQuery,
  useStampParcelsQuery,
  useGiftStampBookMutation,
  useGiftStampBookToNewcomerMutation,
  useWithdrawStampGiftMutation,
  useSetPostagePreferenceMutation,
  useNudgeMutation,
  useAcknowledgeMutation,
  useAttachPhotoMutation,
  useTakeBackPhotoMutation,
  useCaptionPhotoMutation,
} = lettersApi;